import { Component, inject, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	CondoUnit,
	DefaultMatterAdjustmentCommonExpenses,
	MatterAdjustmentCommonExpenses,
	UnitType,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { Select, Store } from '@ngxs/store';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { getDate, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { calculateCommonExpenses } from '../../../../shared/services/adjustments';
import { ICON } from '@core/util/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';
import { TestHooks } from './common-expenses-dialog.hooks';
import { AdjustmentData } from '../adjustments.types';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { Logger } from '@core/logger';
import { MatterTitlesDispatch } from '@state/matter/titles/titles.dispatch';
import { createId } from '@core/util/file';
import { SnackbarService } from '@services/ui/snackbar.service';

@UntilDestroy()
@Component({
	selector: 'app-common-expenses-dialog',
	templateUrl: 'common-expenses-dialog.component.html',
	standalone: false,
})
export class CommonExpensesDialogComponent implements OnInit {
	testHooks = TestHooks;
	private store = inject(Store);

	adjustment: MatterAdjustmentCommonExpenses = cloneDeep(
		DefaultMatterAdjustmentCommonExpenses,
	);
	adjustmentDate: string;
	@Select(SelectedMatter.adjustmentDate) adjustmentDate$: Observable<string>;
	@ViewSelectSnapshot(AdjustmentsSelector.commonExpensesAdjustment)
	commonExpensesAdjustment: MatterAdjustmentCommonExpenses[];
	@SelectSnapshot(SelectedMatter.matterHST) hst: number;
	@SelectSnapshot(
		AdjustmentDefaultDetailsSelectors.adjustmentDetailsCommonExpenses,
	)
	public adjustmentDetails: boolean;
	public PropertyUsage = PropertyUsage;

	public disableApplyHstField = signal(false);

	public matterTitles = this.store.selectSignal(MatterTitlesState.getAll);
	public propertyUsage = this.store.selectSignal(SelectedMatter.propertyUsage);

	creditAmount: number;
	hstAmount: number;
	readonly ICON = ICON;
	vendorPaidItems = [
		{
			label: 'Yes',
			value: 'yes',
		},
		{
			label: 'No',
			value: 'no',
		},
	];

	vendorShare: number;
	vendorShareLabel: string;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: AdjustmentData,
		private dialogRef: MatDialogRef<CommonExpensesDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
		private matterTitlesDispatch: MatterTitlesDispatch,
		private snackbar: SnackbarService,
	) {}

	ngOnInit(): void {
		this.adjustment = cloneDeep({
			...DefaultMatterAdjustmentCommonExpenses,
			details: this.adjustmentDetails,
		});

		const existingAdjustment = this.commonExpensesAdjustment.find(
			(adjustment) => adjustment.id === this.data?.adjustmentId,
		);
		if (existingAdjustment) {
			this.adjustment = {
				title: DefaultMatterAdjustmentCommonExpenses.title,
				...cloneDeep(existingAdjustment),
			};
		}

		this.creditAmount = 0;
		this.hstAmount = 0;
		this.vendorShare = 0;
		this.adjustmentDate$.pipe(untilDestroyed(this)).subscribe((date) => {
			this.adjustmentDate = date;
			this.vendorShareLabel = `Vendor's share for ${getDate(parseISO(this.adjustmentDate)) - 1} days`;
			this.changed();
		});

		if (existingAdjustment?.linkedTitleCondoUnitId) {
			const linkedTitleCondoUnit = this.matterTitles().find(
				(x) => x.id === existingAdjustment.linkedTitleCondoUnitId,
			) as CondoUnit | undefined;

			if (linkedTitleCondoUnit?.unitType === UnitType.COMMERCIAL_UNIT) {
				this.disableApplyHstField.set(false);
			} else {
				this.adjustment.applyHst = false;
				this.disableApplyHstField.set(true);
			}
		}
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	changed(): void {
		const commonExpensesCalc = calculateCommonExpenses(
			this.adjustment.fees,
			this.adjustment.applyHst,
			this.adjustment.vendorPaid,
			this.adjustment.vendorPaidAmount,
			this.adjustmentDate,
			this.hst,
		);

		this.creditAmount = commonExpensesCalc.creditAmount;
		this.vendorShare = commonExpensesCalc.vendorShareAmount;
		this.hstAmount = commonExpensesCalc.hstAmount;
		this.adjustment.vendorPaidAmount = commonExpensesCalc.vendorPaidAmount;
	}

	saveModal(): void {
		// Create a new id if this adjustment item is new which is need to create a link to the condo unit below
		if (!this.adjustment.id) {
			this.adjustment.id = createId();
		}

		if (this.adjustment.linkedTitleCondoUnitId) {
			const linkedTitleCondoUnitId = this.adjustment.linkedTitleCondoUnitId;
			const linkedTitleCondoUnit = this.findLinkedTitleCondoUnit(
				linkedTitleCondoUnitId,
			);

			if (!linkedTitleCondoUnit) {
				Logger.error(
					`Error encountered when updating SoAdj common expenses item id ${this.adjustment.id} which is linked to title condo unit id ${linkedTitleCondoUnitId} but no condo unit matching that id was found. SoAdj common expenses item will be unlinked.`,
				);
				this.adjustment.linkedTitleCondoUnitId = null;
			}

			// If SoAdj common expenses item is linked to a title condo unit, update/sync the common expenses amount and HST in the condo unit
			this.updateLinkedTitleCondoUnit(linkedTitleCondoUnit);
		} else {
			// If SoAdj common expenses item is not linked to a title condo unit, search for a suitable condo unit and link to it
			const newLinkedTitleCondoUnitId = this.createLinkToTitleCondoUnit();

			// If a suitable condo unit was found, link the SoAdj common expenses item to it
			if (newLinkedTitleCondoUnitId) {
				this.adjustment.linkedTitleCondoUnitId = newLinkedTitleCondoUnitId;
			}
		}

		this.adjustmentDispatch.Set(this.adjustment);
		this.dialogRef.close();
	}

	updateLinkedTitleCondoUnit(linkedTitleCondoUnit: CondoUnit) {
		if (!linkedTitleCondoUnit) return;

		const shouldUpdateApplyHstField =
			linkedTitleCondoUnit.unitType === UnitType.COMMERCIAL_UNIT;

		const titleCondoUnitPayload: CondoUnit = {
			...linkedTitleCondoUnit,
			commonExpenses: this.adjustment.fees,
		};

		if (shouldUpdateApplyHstField) {
			titleCondoUnitPayload.applyHst = !!this.adjustment.applyHst;
		}

		this.matterTitlesDispatch.CreateOrUpdate(titleCondoUnitPayload);

		this.snackbar.openSnackBar({
			text: 'Common expenses updated and will reflect on property details.',
			icon: ICON.CIRCLE_CHECK,
			type: 'success',
		});
	}

	createLinkToTitleCondoUnit(): string | undefined {
		const linkableTitleCondoUnit = this.findLinkableTitleCondoUnit();

		if (!linkableTitleCondoUnit) {
			// No suitable condo unit found to link to
			return;
		}

		const shouldUpdateApplyHstField =
			linkableTitleCondoUnit.unitType === UnitType.COMMERCIAL_UNIT;

		const titleCondoUnitPayload: CondoUnit = {
			...linkableTitleCondoUnit,
			linkedSoAdjCommonExpensesId: this.adjustment.id,
			commonExpenses: this.adjustment.fees,
		};

		if (shouldUpdateApplyHstField) {
			titleCondoUnitPayload.applyHst = !!this.adjustment.applyHst;
		}

		this.matterTitlesDispatch.CreateOrUpdate(titleCondoUnitPayload);

		this.snackbar.openSnackBar({
			text: 'Common expenses updated and will reflect on property details.',
			icon: ICON.CIRCLE_CHECK,
			type: 'success',
		});

		return linkableTitleCondoUnit.id;
	}

	findLinkedTitleCondoUnit(
		linkedTitleCondoUnitId: string,
	): CondoUnit | undefined {
		const linkedTitleCondoUnit = this.matterTitles().find(
			(x) => x.id === linkedTitleCondoUnitId,
		);
		return linkedTitleCondoUnit as CondoUnit | undefined;
	}

	findLinkableTitleCondoUnit(): CondoUnit | undefined {
		const searchForCommercialCondoUnit =
			this.propertyUsage() === PropertyUsage.COMMERCIAL ||
			this.adjustment.applyHst === true;

		if (searchForCommercialCondoUnit) {
			const candidateCondoUnit = this.matterTitles().find(
				(x: CondoUnit) =>
					x.primary &&
					!x.linkedSoAdjCommonExpensesId &&
					x.unitType === UnitType.COMMERCIAL_UNIT &&
					x.commonExpenses === 0,
			);

			return candidateCondoUnit as CondoUnit | undefined;
		}

		const candidateCondoUnit = this.matterTitles().find(
			(x: CondoUnit) =>
				x.primary &&
				!x.linkedSoAdjCommonExpensesId &&
				x.unitType === UnitType.DWELLING_UNIT &&
				x.commonExpenses === 0,
		);

		return candidateCondoUnit as CondoUnit | undefined;
	}
}
